<template>
  <div>
    <PrimeDataTable
      ref="dataTable"
      :tableConfig="tableConfig"
      :tableOptions="tableOptions"
      :page-size="pageSize"
      :loadDataFunction="loadUserDocuments"
      @selectedColumns="tableToggleData"
      :paginator="true"
    />
  </div>
</template>

<script>
// import DocumentType from '@/assets/js/enum/DocumentType';
import { defineAsyncComponent } from 'vue';
import DocumentStatus from '@/assets/js/enum/DocumentStatus';
import PermissionLevel from '@/assets/js/enum/PermissionLevel';
import { roundStatusDisplayName } from '@/assets/js/enum/RoundStatus';

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { xhr } from '@/xhr/endpoints';
import { DEBATE_HOST_NAME_MAX_CHARS } from '@/assets/js/constants';

export default {
  name: 'UserDocuments',
  components: {
    PrimeDataTable: defineAsyncComponent(() => import('@/components/table/PrimeDataTable.vue')),
  },
  props: {
    adminMode: Boolean,
    loadDataFunction: Function,
  },
  async mounted() {
    //
    // Load Debate Host options
    // load organization names
    (await xhr.getUserOrganizationNames(this.idToken)).map(x => {
      if (x && x.name) {
        this.userOrganizationNames.push(x.name);
      }
    });
  },
  data() {
    return {
      pageSize: 5,
      userOrganizationNames: [],
      documentOwnersNames: [],
      documentPublication: [],
      documentAuthorName: [],
      documentRound: [],
      documentCounterpart: [],
      documentStatus: [],
      permissionLevelFilter: [],
    };
  },
  computed: {
    ...mapState(['idToken', 'tooltips']),

    ...mapGetters(['formatDate', 'tableToggleData']),

    tableOptions() {
      return {
        globalFilterFields: [
          'document.title',
          'oppositionTeam.teamLead',
          'document.authorName',
          'owner.username',
          'document.publication',
          'document.articleTitle',
        ],
        archiveSelected: {
          action: async ({ items, success, failed }) => {
            await this.archiveMultipleDocuments(items, success, failed, true);
          },
          title: 'Archive Documents',
          tooltip: 'Archive the checked items',
        },
        restoreSelected: {
          action: async ({ items, success, failed }) => {
            await this.archiveMultipleDocuments(items, success, failed, false);
          },
          title: 'Restore Documents',
          tooltip: 'Restore archived items',
        },
        customFields: {
          field: 'team.custom_fields',
        },
        tableName: 'tableUserDocuments',
        headerConfig: [
          [
            {
              header: 'Insights',
            },
            {
              header: 'Debate Title',
              field: 'document.title',
              colspan: 2,
              sortable: true,
            },
            {
              header: 'Citations',
            },
            {
              header: 'Sub debates',
            },
            {
              header: 'Counterpart',
            },
            {
              header: 'Round',
              field: 'document.team_latest_round',
            },
            {
              header: 'Timer',
              field: 'document.OpenRoundTimer',
              // sortable: true,
            },
            {
              header: 'Status',
            },
            {
              header: 'Team Status',
            },
            {
              header: 'My Role',
              field: 'permissionLevel',
              sortable: true,
            },
            {
              header: 'Author',
            },
            {
              header: 'Publisher',
            },
            {
              header: 'Debate Host',
            },
          ],
        ],
      };
    },
    tableConfig() {
      const tableConfig = [
        {
          column: 'Insights',
          columnTooltip: 'See debate insights',
          customIcon: '/icons/icon-Information.svg',
          customIconLink: this.adminMode
            ? undefined
            : row => `/document-details/${row.document._id}`,
          customIconAction: this.adminMode
            ? row => this.$emit('adminModeNavigate', `/document-details/${row.document._id}`)
            : undefined,
          center: true,
          unSelectable: true,
          marginSpace: true,
          paddingSpace: true,
          index: 1,
        },
        {
          column: 'Debate Title',
          field: 'document.title',
          model: row => {
            const title =
              row.document.status === DocumentStatus.DRAFT
                ? row.document.title + ' (Draft)'
                : row.document.title;
            return title;
          },
          columnTooltip: 'See current round',
          link: row => {
            return row.permissionLevel === PermissionLevel.COHORT ||
              row.permissionLevel === PermissionLevel.CURATOR
              ? this.$router.routesMap.CuratorDebate(row.document?._id)
              : this.$router.routesMap.Debate(row.document?._id);
          },
          action: this.adminMode
            ? row =>
                this.$emit('adminModeNavigate', this.$router.routesMap.Debate(row.document._id))
            : undefined,
          sortable: true,
          ellipsis: true,
          unSelectable: true,
          index: 2,
        },
        {
          column: 'Citations',
          field: 'document.citation',
          columnTooltip: "Mine/Counterpart's",
          model: row => {
            return row.oppositionTeam?.id === row.document.owner.id
              ? `${row.oppoCitations || 0}/${row.hostCitations || 0}`
              : `${row.hostCitations || 0}/${row.oppoCitations || 0}`;
          },
          center: true,
          paddingSpace: true,
          index: 3,
        },
        {
          column: 'Subdebates',
          field: 'subDebates',
          model: row => {
            return row.subDebates;
          },
          center: true,
          paddingSpace: true,
          index: 4,
        },
        {
          column: 'Counterpart',
          field: 'oppositionTeam.teamLead',
          model: row => {
            return row.oppositionTeam?.teamLead;
          },
          sortable: true,
          filter: {
            type: 'select',
            options: this.documentCounterpart,
          },
          paddingSpace: true,
          index: 5,
        },
        {
          column: 'Round',
          field: 'team_latest_round',
          model: row => {
            return `${row.team_latest_round}`;
          },
          center: true,
          sortable: true,
          border: true,
          filter: {
            type: 'select',
            options: this.documentRound,
          },
          paddingSpace: true,
          index: 6,
        },
        {
          column: 'Timer',
          columnTooltip: 'DD&#58;HH&#58;MM since the round opened',
          field: 'document.OpenRoundTimer',
          model: row =>
            row.document.OpenRoundTimer
              ? this.getTimeDifference(row.document.OpenRoundTimer)
              : null,
          center: true,
          sortable: true,
          index: 7,
          timer: true,
        },

        {
          column: 'Status',
          field: 'team_round_status',
          fieldTooltip: item => {
            switch (item.split('-')[1]) {
              case 'Framing':
                return 'Team leadership lays the groundwork for the discussion.';
              case 'Drafting':
                return 'Team members recommend their best arguments.';
              case 'Finalizing':
                return 'Team leadership finalizes replies for the round.';
              case 'Published':
                return 'Round is closed. Waiting for replies in the next round.';
            }
          },
          model: row => {
            return `${roundStatusDisplayName(row.team_round_status)}`;
          },
          sortable: true,
          filter: {
            type: 'select',
            options: this.documentStatus,
          },
          index: 8,
        },
        {
          column: 'Team Status',
          field: 'team',
          icon: 'user',
          customIcons: '/icons/black/icon-black-inlinerules.svg',
          fieldTooltip: () => 'Open Team Lineup',
          model: row => row.team?.member_count || 0,
          link: this.adminMode
            ? undefined
            : row =>
                row.permissionLevel !== PermissionLevel.COHORT
                  ? `/team-settings/${row.document?._id}/${row.team?._id}`
                  : '',
          action: this.adminMode
            ? row =>
                row.permissionLevel !== PermissionLevel.COHORT
                  ? this.$emit(
                      'adminModeNavigate',
                      `/team-settings/${row.document?._id}/${row.team?._id}`
                    )
                  : ''
            : undefined,
          position: 'left',
          center: true,
          teamCenter: true,
          border: true,
          index: 9,
        },

        {
          column: 'My Role',
          field: 'permissionLevel',
          sortable: true,
          model: row => PermissionLevel[row.permissionLevel.replace(' ', '_').toUpperCase()],
          filter: {
            type: 'select',
            options: this.permissionLevelFilter,
          },
          unChecked: true,
          index: 10,
        },
        {
          column: 'Content Title',
          field: 'document.articleTitle',
          sortable: true,
          model: row => {
            return row.document.articleTitle;
          },
          ellipsis: true,
          index: 11,
        },
        {
          column: 'Author',
          field: 'document.authorName',
          model: row => {
            return row.document.authorName;
          },
          filter: {
            type: 'select',
            options: this.documentAuthorName,
          },
          sortable: true,
          ellipsis: true,
          index: 12,
        },

        {
          column: 'Publisher',
          field: 'document.publication',
          model: row => {
            return row.document.publication;
          },
          filter: {
            type: 'select',
            options: this.documentPublication,
          },
          sortable: true,
          ellipsis: true,
          index: 13,
        },

        {
          column: 'Debate Host',
          field: 'owner.username',
          model: row => {
            return row.document?.owner?.name?.length > DEBATE_HOST_NAME_MAX_CHARS
              ? `${row.document?.owner?.name?.substring(0, DEBATE_HOST_NAME_MAX_CHARS)}...`
              : row.document?.owner?.name;
          },
          filter: {
            type: 'select',
            options: [...this.documentOwnersNames, ...this.userOrganizationNames],
          },
          sortable: true,
          unChecked: true,
          index: 14,
        },

        {
          column: '',
          field: 'archived',
          filter: {
            type: 'checkbox',
            text: 'Show Archived Debates',
          },
        },
      ];
      return tableConfig;
    },
  },

  methods: {
    ...mapMutations(['removeUserDocuments']),

    ...mapActions(['updateUserDocument']),

    async loadUserDocuments({ page, resultsPerPage, sortColumn, sortDirection, filters }) {
      let res = await this.loadDataFunction({
        page,
        resultsPerPage,
        sortColumn,
        sortDirection,
        filters,
      });
      if (res.filters) {
        this.documentOwnersNames = res.filters['document.authorName'] || [];
        this.documentPublication = res.filters['document.publication'] || [];
        this.documentAuthorName = res.filters['document.authorName'] || [];
        this.documentRound = res.filters.team_latest_round.sort() || [];
        this.documentCounterpart = res.filters['oppositionTeam.teamLead'] || [];
        this.documentStatus = res.filters.team_round_status || [];
        this.permissionLevelFilter = res.filters.permissionLevel || [];
      } else {
        console.error('Filters not defined in the response');
        this.documentOwnersNames = [];
        this.documentPublication = [];
        this.documentAuthorName = [];
        this.documentRound = [];
        this.documentCounterpart = [];
        this.documentStatus = [];
        this.permissionLevelFilter = [];
      }
      return res;
    },

    getTimeDifference(date) {
      let date_past = new Date(date);
      let date_now = new Date();
      let seconds = Math.floor((date_now - date_past) / 1000);
      let minutes = Math.floor(seconds / 60);
      let hours = Math.floor(minutes / 60);
      let days = Math.floor(hours / 24);

      hours = hours - days * 24;
      minutes = minutes - days * 24 * 60 - hours * 60;
      seconds = seconds - hours * 60 * 60 - minutes * 60;
      if (hours < 10) {
        hours = '0' + hours;
      }
      if (minutes < 10) {
        minutes = '0' + minutes;
      }
      if (days < 10) {
        days = '0' + days;
      }
      return `${days}:${hours}:${minutes}`;
    },

    async archiveMultipleDocuments(items, success, failed, archive) {
      try {
        for (let i = 0; i < items.length; i++) {
          await this.updateUserDocument(
            { documentId: items[i].document._id, archive },
            this.idToken
          );
        }
        if (success) success();
        this.$refs.dataTable.loadData();
      } catch (e) {
        console.log(e);
        if (failed) failed();
      }
    },
  },
};
</script>
